/**
 * Fingerprint Service — FingerprintJS v4 open-source SDK wrapper.
 *
 * MAESTRO:CONACCESS P2 — per MDM-CONACCESS-001 Section 18 P2.
 * Produces the visitor ID sent as `X-Maestro-Fingerprint-Hash` on anonymous
 * grading requests. Cached in-memory + localStorage so repeated grades don't
 * re-compute.
 *
 * Per proposal Section 18 P2: uses the open-source SDK (no API key, no
 * external service calls). Visitor ID is a client-side-derived hash.
 */

const STORAGE_KEY = 'maestro_fingerprint_hash';

let _cachedHash = null;
let _cachedPromise = null;

/**
 * Read the cached fingerprint hash from localStorage.
 * @returns {string|null}
 */
function readStored() {
  try {
    return typeof window !== 'undefined' && window.localStorage
      ? window.localStorage.getItem(STORAGE_KEY)
      : null;
  } catch {
    return null;
  }
}

/**
 * Persist the fingerprint hash to localStorage (best-effort).
 * @param {string} hash
 */
function writeStored(hash) {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(STORAGE_KEY, hash);
    }
  } catch {
    /* ignore quota/permission failures */
  }
}

/**
 * Load FingerprintJS v4 and return the visitor ID.
 * Dynamic import so bundler code-splits the SDK (keeps main bundle small).
 * @returns {Promise<string>} visitor ID (16+ char alphanumeric)
 */
async function computeFingerprint() {
  const mod = await import('@fingerprintjs/fingerprintjs');
  const fpPromise = mod.load({ monitoring: false });
  const fp = await fpPromise;
  const result = await fp.get();
  if (!result || typeof result.visitorId !== 'string' || result.visitorId.length < 16) {
    throw new Error('FingerprintJS returned an invalid visitor ID');
  }
  return result.visitorId;
}

/**
 * Get the visitor fingerprint hash for the current browser.
 * Idempotent within the session + persisted across reloads.
 * @returns {Promise<string>}
 */
export async function getFingerprintHash() {
  if (_cachedHash) return _cachedHash;
  const stored = readStored();
  if (stored && stored.length >= 16) {
    _cachedHash = stored;
    return stored;
  }
  if (_cachedPromise) return _cachedPromise;
  _cachedPromise = (async () => {
    try {
      const hash = await computeFingerprint();
      _cachedHash = hash;
      writeStored(hash);
      return hash;
    } finally {
      _cachedPromise = null;
    }
  })();
  return _cachedPromise;
}

/**
 * Clear the cached fingerprint — used by test fixtures + logout flows.
 */
export function clearFingerprintCache() {
  _cachedHash = null;
  _cachedPromise = null;
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

export const FINGERPRINT_HASH_HEADER = 'X-Maestro-Fingerprint-Hash';
